import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/*
 * r16q16 (owner 11-05: "no way to edit or unsend the already sent messages, which IG normally supports"). During Live, under a message we sent. The phone does it, as a person would: a long
 * press on the message, then its menu. Unsend asks first, as Instagram itself does: it can't be taken back, and the other person may have seen it already.
 */
const LINK = 'tap rounded px-1 text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline';
const BUTTON = 'tap rounded-lg border border-border bg-secondary/60 px-2.5 py-1 text-xs font-medium hover:bg-secondary disabled:opacity-50';

export default function MessageActions({ m, onAct, status }) {
  const [mode, setMode] = useState(null);
  const [text, setText] = useState('');
  const canEdit = m.kind === 'text' && typeof m.text === 'string' && m.text !== '';

  if (status && status.pending) {
    return (
      <div className="mt-1 flex items-center justify-end gap-1.5 px-1 text-[11px] text-muted-foreground" role="status" data-testid="msg-action-pending">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        {status.pending === 'edit' ? 'Editing it on the phone' : 'Unsending it on the phone'}
      </div>
    );
  }

  if (mode === 'unsend') {
    return (
      <div className="mt-1 flex flex-wrap items-center justify-end gap-2 px-1 text-xs" data-testid="msg-unsend-ask">
        <span className="text-muted-foreground">Unsend for everyone?</span>
        <button type="button" className={cn(BUTTON, 'border-destructive/40 text-destructive')} onClick={() => { setMode(null); onAct(m, 'unsend'); }} data-testid="msg-unsend-yes">Unsend</button>
        <button type="button" className={BUTTON} onClick={() => setMode(null)} data-testid="msg-unsend-no">Keep it</button>
      </div>
    );
  }

  if (mode === 'edit') {
    const next = text.trim();
    const save = () => { if (next && next !== m.text) { setMode(null); onAct(m, 'edit', next); } };
    return (
      <div className="mt-1 flex flex-col items-stretch gap-1.5" data-testid="msg-edit">
        <textarea
          className="w-full min-w-[14rem] resize-y rounded-lg border border-border bg-secondary/40 px-2.5 py-1.5 text-sm text-foreground"
          rows={2}
          value={text}
          maxLength={1000}
          onChange={(e) => setText(e.target.value)}
          aria-label="The new text"
          data-testid="msg-edit-box"
        />
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="mr-auto text-[11px] text-muted-foreground">Instagram allows this for 15 minutes after sending.</span>
          <button type="button" className={BUTTON} disabled={!next || next === m.text} onClick={save} data-testid="msg-edit-save">Save</button>
          <button type="button" className={BUTTON} onClick={() => setMode(null)} data-testid="msg-edit-cancel">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-0.5 flex flex-col items-end">
      {status && status.error && <p className="px-1 text-[11px] text-destructive" role="alert" data-testid="msg-action-error">{status.error}</p>}
      <div className="flex items-center justify-end gap-1 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100" data-testid="msg-actions">
        {canEdit && <button type="button" className={LINK} onClick={() => { setText(m.text); setMode('edit'); }} data-testid="msg-edit-open">Edit</button>}
        <button type="button" className={LINK} onClick={() => setMode('unsend')} data-testid="msg-unsend-open">Unsend</button>
      </div>
    </div>
  );
}
